import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import { useSelector } from "react-redux";
import axios from "utils/http";
import { getOrganizationIdFromWorkspaceKey } from "utils/workspaceRequest";
import { collectReportContext } from "./reportContext";
import { captureViewport, captureScreen, canCaptureScreen, dataUrlToBlob } from "./screenshot";

// The one Help & feedback dialog (docs/help-feedback-plan.md). Opened by the launcher (floating
// button, sidebar) or by openFeedback({ kind, message, context }) from an error surface.
//
// Light surface, so every control carries explicit dark ink (CLAUDE.md: the palette mode is
// "dark" and unstyled text on a white card is invisible).

const API_BASE = process.env.REACT_APP_BASE_URL;
export const KINDS = [
  { id: "problem", label: "Something failed" },
  { id: "wrong_result", label: "Wrong result" },
  { id: "question", label: "Question" },
  { id: "suggestion", label: "Suggestion" },
];
export const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;
const INK = "#0f172a";
const MUTED = "#475569";

const fieldSx = {
  "& .MuiInputBase-input": { color: INK },
  "& .MuiInputLabel-root": { color: MUTED },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
};

const fmtBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const FeedbackDialog = ({ open, onClose, prefill = null, screenshotDataUrl: initialShot = null, shotError: initialShotError = "", shotPending = false }) => {
  const user = useSelector((state) => state.auth?.user);
  const selectedWorkspaceKey = useSelector((state) => state.workspace?.selectedWorkspaceKey);
  const organizations = useSelector((state) => state.workspace?.organizations);

  const [kind, setKind] = useState("problem");
  const [message, setMessage] = useState("");
  const [shot, setShot] = useState(null); // data URL
  const [shotError, setShotError] = useState("");
  const [shotBusy, setShotBusy] = useState(false);
  const [hiddenForCapture, setHiddenForCapture] = useState(false); // "Capture my screen" hides the dialog meanwhile
  const [files, setFiles] = useState([]);
  const [showContext, setShowContext] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { number }
  const fileInputRef = useRef(null);
  const errorRef = useRef(null);
  // A failed send must be seen: the alert sits at the bottom of a dialog that scrolls on small
  // screens (the 429 "too many reports" reply was invisible in a 720 px window).
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [error]);

  // A fresh open starts from the prefill; a failed send keeps everything the user typed.
  // Only `open` resets the form — the screenshot arrives later and must not wipe the text.
  useEffect(() => {
    if (!open) return;
    setKind(KINDS.some((k) => k.id === prefill?.kind) ? prefill.kind : "problem");
    setMessage(prefill?.message || "");
    setFiles([]);
    setError("");
    setResult(null);
    setShowContext(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // The launcher's capture (automatic, in the background) feeds the preview; the user's own
  // Retake / Remove / Capture my screen take over from there.
  useEffect(() => {
    if (!open) return;
    setShot(initialShot || null);
    setShotError(initialShotError || "");
    setShotBusy(Boolean(shotPending));
  }, [open, initialShot, initialShotError, shotPending]);

  // Workspace keys are "personal" or "org:<organizationId>" (state/slices/workspaceSlice.js);
  // the same helper the API requests use decides which, so the two can never disagree.
  const workspace = useMemo(() => {
    const key = selectedWorkspaceKey || "personal";
    const orgId = getOrganizationIdFromWorkspaceKey(key);
    const org = orgId ? (organizations || []).find((o) => String(o?._id || o?.id) === orgId) : null;
    return { key, organizationId: orgId, organizationName: org?.name || null };
  }, [selectedWorkspaceKey, organizations]);

  const context = useMemo(() => {
    if (!open) return null;
    try {
      const base = collectReportContext({ user, workspace });
      return prefill?.context ? { ...base, reportedFrom: prefill.context } : base;
    } catch (e) {
      return { collectError: e?.message || String(e) };
    }
    // Re-collected on open and after each send attempt so the error buffer is current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, workspace, prefill, sending]);

  const contextSummary = useMemo(() => {
    if (!context) return [];
    const rows = [];
    rows.push(`You: ${context.user?.name || ""} ${context.user?.email ? `<${context.user.email}>` : ""}`.trim());
    rows.push(`Workspace: ${context.workspace?.organizationName || "personal"}`);
    rows.push(`Page: ${context.page?.url || ""}`);
    if (context.project) rows.push(`Project: ${context.project.name || context.project.id || ""}`);
    if (context.scenario) rows.push(`Scenario: ${context.scenario.name || context.scenario.id || ""} (${context.scenario.status || "—"})`);
    rows.push(`Browser: ${context.browser?.platform || ""} · ${context.browser?.screen || ""}`);
    rows.push(`App build: ${context.app?.buildId || ""}`);
    rows.push(`Recent client errors: ${context.clientErrors?.length || 0} · failed requests: ${context.failedRequests?.length || 0}`);
    return rows;
  }, [context]);

  const totalBytes = useMemo(() => files.reduce((n, f) => n + f.size, 0) + (shot ? Math.round((shot.length * 3) / 4) : 0), [files, shot]);
  const overLimit = totalBytes > MAX_TOTAL_BYTES;

  const retake = useCallback(async () => {
    setShotBusy(true);
    setShotError("");
    try {
      // The dialog is marked data-no-screenshot, so the page behind it is what gets captured.
      setShot(await captureViewport());
    } catch (e) {
      setShot(null);
      setShotError("The automatic screenshot did not work on this page. You can capture your screen instead, or attach an image.");
    } finally {
      setShotBusy(false);
    }
  }, []);

  const captureWholeScreen = useCallback(async () => {
    setShotBusy(true);
    setShotError("");
    setHiddenForCapture(true);
    try {
      setShot(await captureScreen());
    } catch (e) {
      if (!/NotAllowed|Permission|abort/i.test(e?.name || e?.message || "")) {
        setShotError("Screen capture did not work in this browser. You can attach an image instead.");
      }
    } finally {
      setHiddenForCapture(false);
      setShotBusy(false);
    }
  }, []);

  const addFiles = (list) => {
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (next.length >= MAX_ATTACHMENTS) break;
      next.push(f);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const send = async () => {
    if (!message.trim()) {
      setError("Please describe what happened, in a sentence or two.");
      return;
    }
    if (overLimit) {
      setError(`The screenshot and files are too large together (${fmtBytes(totalBytes)}). The limit is 25 MB.`);
      return;
    }
    setSending(true);
    setError("");
    try {
      const form = new FormData();
      form.append("kind", kind);
      form.append("message", message.trim());
      // Collected at Send, not at open, so the error buffer includes anything that failed
      // while the dialog was open.
      const fresh = collectReportContext({ user, workspace });
      form.append("context", JSON.stringify(prefill?.context ? { ...fresh, reportedFrom: prefill.context } : fresh));
      if (shot) form.append("screenshots", await dataUrlToBlob(shot), "screenshot.png");
      files.forEach((f) => form.append("attachments", f, f.name));
      const { data } = await axios.post(`${API_BASE}/support/reports`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ number: data?.number });
    } catch (e) {
      const status = e?.response?.status;
      setError(
        e?.response?.data?.message ||
          (status === 413 ? "The files are too large. The limit is 25 MB in total." : "The report could not be sent. Your text and screenshot are still here — please try again."),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-no-screenshot=""
      data-testid="feedback-dialog"
      sx={{ visibility: hiddenForCapture ? "hidden" : "visible" }}
      PaperProps={{ sx: { background: "#ffffff", color: INK } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", color: INK, fontSize: "1.05rem", pb: 1 }}>
        {result ? "Thank you" : "Help & feedback"}
        <IconButton aria-label="Close" onClick={onClose} disabled={sending} size="small" sx={{ color: MUTED }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ color: INK }}>
        {result ? (
          <Box data-testid="feedback-success" sx={{ py: 1 }}>
            <Typography sx={{ color: INK, mb: 1 }}>
              We received your report <strong>#{result.number}</strong>. A confirmation is on its way to your email, and we reply there, usually within one business day.
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED }}>
              If you want to add anything, reply to that email and quote the report number.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: MUTED, mb: 1.5 }}>
              Tell us what happened, or what you need. We get the page, project and scenario details automatically, so you do not have to describe them.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {KINDS.map((k) => (
                <Chip
                  key={k.id}
                  label={k.label}
                  clickable
                  onClick={() => setKind(k.id)}
                  data-testid={`feedback-kind-${k.id}`}
                  color={kind === k.id ? "primary" : "default"}
                  variant={kind === k.id ? "filled" : "outlined"}
                  sx={kind === k.id ? {} : { color: INK, borderColor: "#cbd5e1" }}
                />
              ))}
            </Box>
            <TextField
              label="What happened?"
              placeholder="What were you doing, what did you expect, and what did you see instead?"
              multiline
              minRows={4}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputProps={{ "data-testid": "feedback-message", maxLength: 10000 }}
              sx={fieldSx}
            />

            <Box sx={{ mt: 2, p: 1.5, border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#f8fafc" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>
                  Screenshot {shot ? "" : "(none)"}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  <Button size="small" startIcon={<PhotoCameraIcon />} onClick={retake} disabled={shotBusy || sending} data-testid="feedback-retake" sx={{ color: MUTED, textTransform: "none" }}>
                    {shot ? "Retake" : "Take screenshot"}
                  </Button>
                  {canCaptureScreen() && (
                    <Button size="small" startIcon={<ScreenshotMonitorIcon />} onClick={captureWholeScreen} disabled={shotBusy || sending} data-testid="feedback-capture-screen" sx={{ color: MUTED, textTransform: "none" }}>
                      Capture my screen
                    </Button>
                  )}
                  {shot && (
                    <Button size="small" onClick={() => setShot(null)} disabled={shotBusy || sending} data-testid="feedback-remove-screenshot" sx={{ color: MUTED, textTransform: "none" }}>
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
              {shotBusy ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" sx={{ color: MUTED }}>Capturing…</Typography>
                </Box>
              ) : shot ? (
                <Box sx={{ mt: 1 }}>
                  <img src={shot} alt="Screenshot preview" data-testid="feedback-screenshot-preview" style={{ maxWidth: "100%", maxHeight: 220, border: "1px solid #cbd5e1", borderRadius: 4, display: "block" }} />
                </Box>
              ) : shotError ? (
                <Typography variant="body2" sx={{ color: "#92400e", mt: 1 }} data-testid="feedback-screenshot-error">{shotError}</Typography>
              ) : (
                <Typography variant="body2" sx={{ color: MUTED, mt: 1 }}>No screenshot will be sent.</Typography>
              )}
            </Box>

            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" startIcon={<AttachFileIcon />} onClick={() => fileInputRef.current?.click()} disabled={sending || files.length >= MAX_ATTACHMENTS} sx={{ color: MUTED, textTransform: "none" }}>
                Attach files
              </Button>
              <input ref={fileInputRef} type="file" multiple hidden data-testid="feedback-attach" onChange={(e) => addFiles(e.target.files)} />
              {files.map((f, i) => (
                <Chip key={`${f.name}-${i}`} label={`${f.name} (${fmtBytes(f.size)})`} onDelete={() => setFiles(files.filter((_, j) => j !== i))} size="small" sx={{ color: INK }} />
              ))}
              <Typography variant="caption" sx={{ color: overLimit ? "#b42318" : MUTED }} data-testid="feedback-size">
                {fmtBytes(totalBytes)} of 25 MB
              </Typography>
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Link component="button" type="button" onClick={() => setShowContext((v) => !v)} underline="hover" sx={{ color: MUTED, fontSize: "0.8rem", display: "inline-flex", alignItems: "center" }} data-testid="feedback-context-toggle">
                What we send with your report {showContext ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Link>
              <Collapse in={showContext}>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, color: MUTED, fontSize: "0.78rem" }} data-testid="feedback-context-list">
                  {contextSummary.map((row) => (
                    <li key={row}>{row}</li>
                  ))}
                  <li>Recent technical errors from this page (messages and request paths only; never passwords, tokens or your files).</li>
                </Box>
              </Collapse>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }} data-testid="feedback-error" ref={errorRef}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result ? (
          <Button variant="contained" onClick={onClose} data-testid="feedback-done">Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={sending} sx={{ color: MUTED }}>Cancel</Button>
            <Button variant="contained" onClick={send} disabled={sending || shotBusy} data-testid="feedback-send">
              {sending ? "Sending…" : "Send report"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
